import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  intlShape,
  injectIntl,
  FormattedMessage,
  FormattedNumber
} from 'react-intl';
import formatTimestamp from '../../../i18n/formatTimestamp';

import {
  Button,
  DataTable,
  DataTableColumn,
  DataTableCell,
  Dropdown,
  Icon
} from '@salesforce/design-system-react';

import './AttributeList.scss';
import EthicalScan from '../../../common/components/EthicalScan/EthicalScan';

const CustomDataTableCell = ({ children, property, ...props }) => {
  switch (property) {
    case 'required':
      const requiredKey = `components.ecs.attributes.${children}`;
      return (
        <td className={`attribute-cell wrap-text`}>
          <FormattedMessage id={requiredKey} />
        </td>
      );
    case 'dataType':
      const dataTypeKey = `components.ecs.attributes.${children}`;
      return (
        <td className={`attribute-cell wrap-text`}>
          {children ? (
            <FormattedMessage id={dataTypeKey.toLowerCase()} />
          ) : null}
        </td>
      );
    case 'values':
      return (
        <td className={`attribute-cell wrap-text`}>
          <FormattedNumber value={children || 0} />
        </td>
      );
    case 'edit':
      return (
        <td className={`attribute-cell wrap-text`}>
          <Button
            iconName="edit"
            iconCategory="utility"
            variant="icon"
            disabled={props.item.locked}
            onClick={() => props.onAction('edit', props.item)}
          />
        </td>
      );
    case 'actions':
      const actions = [
        {
          label: props.formatMessage({
            id: 'components.ecs.assetBrowser.menu.delete'
          }),
          value: 'delete'
        }
      ];

      const dropDown = (
        <Dropdown
          assistiveText={{ icon: 'Attribute Options' }}
          buttonVariant="icon"
          iconCategory="utility"
          iconName="down"
          iconVariant="border-filled"
          align="right"
          className="actions-dropdown"
          width="xx-small"
          options={actions}
          disabled={props.item.in_use || props.item.locked}
          onSelect={({ value }) => props.onAction(value, props.item)}
        />
      );
      return <td className={`attribute-cell wrap-text`}>{dropDown}</td>;
    case 'name':
      return (
        <td className={`attribute-cell wrap-text`}>
          {props.item.locked ? (
            <div className="locked-cell">
              <span>{children}</span>
              <Icon
                assistiveText={{ label: 'Lock' }}
                category="utility"
                colorVariant="light"
                name="lock"
                size="x-small"
              />
            </div>
          ) : (
            children
          )}
          {<EthicalScan {...props.item.scan_result} />}
        </td>
      );
    default: {
      return <td className={`attribute-cell wrap-text`}>{children}</td>;
    }
  }
};

CustomDataTableCell.displayName = DataTableCell.displayName;
CustomDataTableCell.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object
  ]),
  property: PropTypes.string.isRequired,
  onclick: PropTypes.func,
  formatMessage: PropTypes.func,
  item: PropTypes.object
};

class ProfileAttributeTable extends Component {
  headerCell(property, formatMessage) {
    return formatMessage({ id: `components.ecs.attributes.${property}` });
  }

  tableData(items) {
    const mapped = items.map(item => ({
      ...item,
      id: item.name,
      dataType: item.data_type,
      hasValues: item.values?.length ? 'Y' : 'N',
      valueCount: item.values?.length || 0,
      updatedAt: formatTimestamp(this.props.intl)(item.updated_at)
    }));

    const unlocked = mapped.filter(item => !item.locked);
    const locked = mapped.filter(item => item.locked);
    return [...locked, ...unlocked];
  }

  render() {
    const { attributes } = this.props;
    const { formatMessage } = this.props.intl;
    const items = this.tableData(attributes);
    return (
      <DataTable
        items={items}
        className="attribute-list-table"
        fixedLayout
        fixedHeader
        noRowHover
      >
        {Object.entries(this.props.columns).map((column, index) => {
          const sortable = column[1].sortable;
          const width = column[1].width;
          const property = column[0];
          return (
            <DataTableColumn key={index} sortable={sortable}>
              <CustomDataTableCell
                property={property}
                formatMessage={formatMessage}
                label={this.headerCell(property, formatMessage)}
                width={width}
                onAction={this.props.onListAction}
              />
            </DataTableColumn>
          );
        })}
      </DataTable>
    );
  }
}

ProfileAttributeTable.propTypes = {
  attributes: PropTypes.array.isRequired,
  lockedAttributes: PropTypes.array,
  columns: PropTypes.object.isRequired,
  onListAction: PropTypes.func.isRequired,
  location: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(ProfileAttributeTable);
